import * as React from 'react';
import { useDebounce } from 'react-use';
import { Box, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import { Search, Close } from '@mui/icons-material';

import { SEARCH_RESULT_BUTTON_CLASS } from 'consts';
import { ESearchInputDisplayMode } from 'enums';

import { SearchResults } from './search-results';
import { useFetchSearchResultQuery } from 'store/apis/search.api';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

interface SearchInputProps {
  mode?: ESearchInputDisplayMode;
  setVisible?: (visible: boolean) => void;
}

export const SearchInput = ({ mode = ESearchInputDisplayMode.LARGE, setVisible = () => {} }: SearchInputProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [focused, setFocused] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [skipFetchSearchResult, setSkipFetchSearchResult] = React.useState<boolean>(true);
  const {
    data: results = {
      buildings: [],
      zones: [],
      service_items: [],
      users: [],
    },
    isFetching: loading_results,
  } = useFetchSearchResultQuery({ orgSlug, search }, { skip: skipFetchSearchResult });

  useDebounce(
    () => {
      if (search) {
        setSkipFetchSearchResult(false);
      }
    },
    1000,
    [search],
  );

  const handleBlur = (event: any) => {
    if (event.relatedTarget?.classList.contains(SEARCH_RESULT_BUTTON_CLASS)) {
      return;
    }

    setFocused(false);
  };

  const handleClick = () => {
    setFocused(false);
    setSearch('');
    setVisible(false);
  };

  return (
    <>
      <TextField
        placeholder="Search..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
          endAdornment: search && (
            <InputAdornment position="end">
              <IconButton onClick={() => setSearch('')} size="small">
                <Close fontSize="inherit" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        variant="outlined"
        size="small"
        fullWidth
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        autoComplete="off"
        sx={{
          backgroundColor: 'white',
        }}
      />

      {mode === ESearchInputDisplayMode.SMALL && (
        <Box
          sx={{
            marginTop: 4,
          }}
        >
          <SearchResults results={results} onClick={handleClick} loading={loading_results} />
        </Box>
      )}

      {mode === ESearchInputDisplayMode.LARGE && focused && (
        <Paper
          elevation={6}
          sx={{
            padding: 2,
            position: 'absolute',
            right: 24,
            top: 72,
            maxWidth: '600px',
            width: '100%',
          }}
        >
          <Typography variant="h6" sx={{ marginBottom: 2 }}>
            Search Results
          </Typography>
          <SearchResults results={results} onClick={handleClick} loading={loading_results} />
        </Paper>
      )}
    </>
  );
};
